import { t } from '@app/i18n';
import type { components } from '@app/api/types';

export type SimulateRequest = components['schemas']['SimulateRequest'];
export type RpcMethod = SimulateRequest['rpc_method'];
export type ClaimsObject = NonNullable<SimulateRequest['claims']>;

/**
 * Métodos que o contrato aceita (L13). Nada de `<select>` nativo (FR-129).
 *
 * **Função, e não constante**, como as outras listas de opção desta tela: uma
 * const de módulo é avaliada no import, e o `t()` dentro dela devolveria para
 * sempre o idioma do primeiro render — o seletor de idioma trocaria a tela
 * inteira e deixaria estas dicas em português, sem nada ficar vermelho.
 *
 * `tools/call` e `tools/list` são nomes do protocolo: são o rótulo nos dois
 * idiomas, e só a dica se traduz.
 */
export function methodOptions(target: TargetMode = 'tool'): { value: RpcMethod; label: string; hint: string }[] {
  if (target === 'agent') {
    return [
      { value: 'SendMessage', label: 'SendMessage', hint: t('simulator.method.sendMessage.hint') },
      { value: 'SendStreamingMessage', label: 'SendStreamingMessage', hint: t('simulator.method.sendStreaming.hint') },
      { value: 'GetTask', label: 'GetTask', hint: t('simulator.method.getTask.hint') },
      { value: 'CancelTask', label: 'CancelTask', hint: t('simulator.method.cancelTask.hint') },
      { value: 'agent-card', label: 'agent-card', hint: t('simulator.method.agentCard.hint') },
    ];
  }
  return [
    { value: 'tools/call', label: 'tools/call', hint: t('simulator.method.toolsCall.hint') },
    { value: 'tools/list', label: 'tools/list', hint: t('simulator.method.toolsList.hint') },
    { value: 'server/discover', label: 'server/discover', hint: t('simulator.method.serverDiscover.hint') },
  ];
}

/**
 * Decision 5 (phase 2): the method depends on the target — the methods the gateway asks the PDP about, the same
 * lists as the backend (`usecases/simulate`) and the executor. A tool: the call and the two discovery methods. A
 * delegation: the A2A 1.0 methods the agents speak, and the card read. Switching the target puts the first method of
 * the new list when the chosen one is not in it: a tool method beside an agent asks what no gateway asks.
 */
export function defaultMethod(target: TargetMode): RpcMethod {
  return target === 'agent' ? 'SendMessage' : 'tools/call';
}

export function methodOfTheTarget(method: RpcMethod, target: TargetMode): boolean {
  return methodOptions(target).some((option) => option.value === method);
}

/**
 * Como se descreve a pessoa da simulação (mrolim, 04/09).
 *
 * `token` é o que sempre existiu: o JWT real, colado. `claims` é o modo novo — o
 * objeto de claims que a pessoa **teria** no JWT, do qual o backend monta um JWT
 * não assinado. Existe porque quem opera tem um perfil na mão, não um token: sem
 * ele, a metade condicional das políticas (a que depende de claim) não tinha como
 * ser simulada, e é justamente a metade cujo veredito não se prevê de cabeça.
 *
 * Os dois são exclusivos no contrato (422 `tokenOrClaimsNotBoth`), e é por isso
 * que aqui são um MODO e não dois campos preenchíveis ao mesmo tempo: um
 * formulário que aceitasse os dois só descobriria o erro depois do POST.
 */
export type SubjectMode = 'token' | 'claims';

/**
 * O que a chamada cita: uma **tool** ou um **agente alvo** (DEF-29, 09/09).
 *
 * Até 08/09 perguntar «`atendimento` pode chamar `cobranca`?» só era possível
 * escrevendo `cobranca` no campo `tool` — e a resposta vinha correta e inútil:
 * DENY pelo eixo workload, sobre um contrato de tool que a chamada nem citava.
 * Com a delegação virando capability, a pergunta tem campo próprio e a resposta
 * cruza os MESMOS dois eixos de uma tool: a pessoa pode E a identidade pode.
 *
 * É um MODO, e não dois campos preenchíveis, pela mesma razão do sujeito: `tool`
 * e `agent` são mutuamente exclusivos no contrato (422 `toolOrAgentNotBoth`), e
 * um formulário que aceitasse os dois só descobriria isso depois do POST.
 */
export type TargetMode = 'tool' | 'agent';

export function targetModeOptions(): { value: TargetMode; label: string; hint: string }[] {
  return [
    {
      value: 'tool',
      label: t('simulator.targetMode.tool.label'),
      hint: t('simulator.targetMode.tool.hint'),
    },
    {
      value: 'agent',
      label: t('simulator.targetMode.agent.label'),
      hint: t('simulator.targetMode.agent.hint'),
    },
  ];
}

export function subjectModeOptions(): { value: SubjectMode; label: string; hint: string }[] {
  return [
    {
      value: 'token',
      label: t('simulator.subjectMode.token.label'),
      hint: t('simulator.subjectMode.token.hint'),
    },
    {
      value: 'claims',
      label: t('simulator.subjectMode.claims.label'),
      hint: t('simulator.subjectMode.claims.hint'),
    },
  ];
}

/**
 * O que o botão **Exemplo** escreve na textarea — e o placeholder dela.
 *
 * Vai ao catálogo, e não fica literal, porque os NOMES das claims são de
 * domínio: um exemplo com `perfil`/`cargo` numa tela em inglês ensina um perfil
 * em português. Funções pela mesma razão das listas acima.
 */
export function claimsExample(): string {
  return t('simulator.claims.example');
}

export function claimsPlaceholder(): string {
  return t('simulator.claims.placeholder');
}

export interface SimulatorFormValues {
  /** UUID da identidade — só as com contrato ativo (409 `sem_contrato_ativo`). No modo A2A é a CHAMADORA. */
  identityId: string;
  /** O que a chamada cita: uma tool, ou um agente alvo. */
  targetMode: TargetMode;
  /** Nome da tool no PDP. Só no modo Tool. */
  tool: string;
  /** Slug do agente alvo. Só no modo Agente (A2A). */
  agent: string;
  rpcMethod: RpcMethod;
  /** Qual descrição de pessoa está em uso. */
  subjectMode: SubjectMode;
  /**
   * JWT da pessoa. **Opcional e nunca persistido**: não vai para cache de query,
   * não entra em auditoria e não é logado. Sem token o PDP nega por falta de
   * sujeito — o que é um resultado legítimo, não um erro de formulário.
   */
  token: string;
  /**
   * As claims como TEXTO — o que o operador digitou, não o objeto.
   *
   * Texto e não objeto de propósito: a textarea é a fonte, e guardar o objeto
   * parseado obrigaria a reparsear a cada tecla e a decidir o que fazer com um
   * JSON pela metade. O parse acontece em dois lugares só: a validação (que
   * mostra o erro) e `simulationBody` (que monta a chamada).
   */
  claims: string;
}

export const simulatorDefaults: SimulatorFormValues = {
  identityId: '',
  targetMode: 'tool',
  tool: '',
  agent: '',
  rpcMethod: 'tools/call',
  subjectMode: 'token',
  token: '',
  claims: '',
};

/** Pré-preenchimento vindo de T17 (**Reproduzir no simulador**), via estado de rota. */
export interface DecisionPreenchimento {
  identityId?: string;
  tool?: string;
  /** Slug do agente destino — uma decisão de delegação (DEF-29). */
  agent?: string;
  /**
   * As claims que a decisão guardou da pessoa (aim-78s). Com elas, o formulário abre no modo Claims (JSON). Nunca o
   * token: o produto não o guarda (regra 12 do CLAUDE.md).
   */
  claims?: ClaimsObject;
}

/** As claims como o formulário as mostra: o JSON com duas casas de recuo, como o botão **Exemplo** escreve. */
export function claimsText(claims: ClaimsObject): string {
  return JSON.stringify(claims, null, 2);
}

/**
 * O `agent` **decide o modo**, e vem antes da tool na leitura: reproduzir uma
 * decisão A2A tem de abrir o formulário na pergunta que aquela decisão fez. Até
 * 08/09 esse botão vinha desabilitado para delegação, com o aviso de que o
 * simulador não as avaliava — deixou de ser verdade hoje.
 */
export function defaultsWithPreenchimento(
  preenchimento: DecisionPreenchimento | null | undefined,
): SimulatorFormValues {
  // A pessoa da decisão (aim-78s): com claims, o modo Claims já preenchido. Sem elas (a chamada sem token, o token que
  // o PDP não aceitou), o modo JWT vazio de sempre, que simula sem pessoa.
  const subject: Partial<SimulatorFormValues> =
    preenchimento?.claims && Object.keys(preenchimento.claims).length > 0
      ? { subjectMode: 'claims', claims: claimsText(preenchimento.claims) }
      : {};
  if (preenchimento?.agent) {
    return {
      ...simulatorDefaults,
      ...(preenchimento.identityId ? { identityId: preenchimento.identityId } : {}),
      targetMode: 'agent',
      agent: preenchimento.agent,
      rpcMethod: defaultMethod('agent'),
      ...subject,
    };
  }
  return {
    ...simulatorDefaults,
    ...(preenchimento?.identityId ? { identityId: preenchimento.identityId } : {}),
    ...(preenchimento?.tool ? { tool: preenchimento.tool } : {}),
    ...subject,
  };
}

/** Por que um texto de claims não serve. */
export type ClaimsProblem =
  'invalidJson' | 'notObject' | 'empty' | 'nameEmpty' | { claim: string; kind: 'valueShape' };

export type ClaimsParse =
  { ok: true; claims: ClaimsObject } | { ok: false; problem: ClaimsProblem };

/**
 * Lê o texto da textarea como objeto de claims.
 *
 * As regras são as do contrato, não invenções da tela: valor é texto, número,
 * booleano ou lista de textos; objeto ou lista aninhados o backend recusa com
 * `claimValueShapeInvalid`, e `null` não é «claim ausente» — ausente é não
 * escrever a chave. Validar aqui não substitui o 422; poupa a ida.
 *
 * Texto vazio é `empty`, e no formulário isso é erro: escolher o modo Claims e
 * deixá-lo em branco é um pedido incompleto, não um pedido sem pessoa. Quem quer
 * simular sem pessoa fica no modo JWT com o campo vazio, como sempre.
 */
export function parseClaims(text: string): ClaimsParse {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, problem: 'empty' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, problem: 'invalidJson' };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, problem: 'notObject' };
  }

  const entries = Object.entries(parsed as Record<string, unknown>);
  if (entries.length === 0) return { ok: false, problem: 'empty' };

  for (const [claim, value] of entries) {
    if (!claim.trim()) return { ok: false, problem: 'nameEmpty' };
    if (!claimValueOk(value)) return { ok: false, problem: { claim, kind: 'valueShape' } };
  }
  return { ok: true, claims: parsed as ClaimsObject };
}

/** Texto, número, booleano ou lista de textos — a forma que uma claim tem num JWT. */
function claimValueOk(value: unknown): boolean {
  // `NaN`/`Infinity` não sobrevivem a `JSON.parse`, então número aqui é número.
  if (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number') {
    return true;
  }
  // Lista vazia passa: `amr: []` é «nenhum método de autenticação», e o PDP sabe
  // responder a isso.
  if (Array.isArray(value)) return value.every((item) => typeof item === 'string');
  return false;
}

/**
 * A frase de erro de cada problema.
 *
 * Já era função antes da i18n, então resolve no idioma de quem chama — é o
 * formato que as consts de texto acima tiveram de adotar.
 */
export function claimsErrorMessage(problem: ClaimsProblem): string {
  if (typeof problem === 'object') {
    return t('simulator.claims.error.valueShape', { claim: problem.claim });
  }
  switch (problem) {
    case 'invalidJson':
      return t('simulator.claims.error.invalidJson');
    case 'notObject':
      return t('simulator.claims.error.notObject', { example: claimsPlaceholder() });
    case 'nameEmpty':
      return t('simulator.claims.error.nameEmpty');
    case 'empty':
    default:
      return t('simulator.claims.error.empty');
  }
}

/**
 * The pair server/tool (decision 13) as the tool field carries it: `<server>/<tool>`, the form the gateway and the
 * Decisions screen show. The server is the gateway id, which has no slash, so the first slash splits the pair. A
 * value without a slash is the name alone (a tool typed by hand, or one that came from Decisions), and the executor
 * finds its server in the contract.
 */
export function toolPair(value: string): { server: string | null; tool: string } {
  const text = value.trim();
  const slash = text.indexOf('/');
  if (slash <= 0) return { server: null, tool: text };
  return { server: text.slice(0, slash), tool: text.slice(slash + 1) };
}

/** The value of the tool field for a pair. */
export function pairValue(server: string, tool: string): string {
  return `${server}/${tool}`;
}

/**
 * Corpo da chamada, na forma do contrato.
 *
 * O modo decide qual sujeito vai, e só um vai: `token` e `claims` juntos são 422
 * (`tokenOrClaimsNotBoth`). Ausência é o que o contrato quer para «não informado»
 * — nem `""` nem `null`. Por isso trocar de modo não precisa limpar o outro
 * campo: o que ficou escrito lá não viaja, e voltar ao modo anterior devolve o
 * que o operador tinha digitado.
 */
export function simulationBody(valores: SimulatorFormValues): SimulateRequest {
  // Exatamente UM alvo: `tool` e `agent` juntos são 422 (`toolOrAgentNotBoth`), e
  // nenhum dos dois também (`toolNameRequired`). Trocar de modo não limpa o
  // outro campo — o que não está no modo escolhido não viaja, e voltar devolve o
  // que o operador tinha digitado, como no sujeito.
  const pair = toolPair(valores.tool);
  const alvo =
    valores.targetMode === 'agent'
      ? { agent: valores.agent.trim() }
      : pair.server
        ? { tool: pair.tool, server: pair.server }
        : { tool: pair.tool };

  const base = {
    identity_id: valores.identityId,
    ...alvo,
    rpc_method: valores.rpcMethod,
  };

  if (valores.subjectMode === 'claims') {
    const parsed = parseClaims(valores.claims);
    // Claims fora de forma não montam corpo: a validação já barrou o submit, e
    // mandar o texto cru faria o backend recusar por outro motivo.
    return parsed.ok ? { ...base, claims: parsed.claims } : base;
  }
  return valores.token.trim() ? { ...base, token: valores.token.trim() } : base;
}
